export type ShowcaseMediaValue =
  | { kind: "image"; src: string; width: number; height: number }
  | { kind: "video"; src: string; poster: string };

export type ShowcaseMediaMap = Readonly<Record<string, ShowcaseMediaValue>>;

export function isShowcaseMediaKey(key: string): boolean {
  return key.length <= 128 && /^(home|sandbox)\.[a-z0-9]+(?:[.-][a-z0-9]+)*$/.test(key);
}

function publicMediaUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 2048) return false;
  try {
    const url = new URL(value);
    // Published objects need permanent public URLs, never expiring signatures or credentials.
    return url.protocol === "https:" && !url.username && !url.password && !url.search && !url.hash;
  } catch { return false; }
}

/** Only expose the fields used by public display; storage metadata stays private. */
export function parseShowcaseMediaValue(value: unknown): ShowcaseMediaValue | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const row = value as Record<string, unknown>;
  if (!publicMediaUrl(row.src)) return undefined;
  if (row.kind === "video" && publicMediaUrl(row.poster)) {
    return { kind: "video", src: row.src, poster: row.poster };
  }
  if (row.kind === "image" && typeof row.width === "number" && typeof row.height === "number"
    && Number.isSafeInteger(row.width) && row.width > 0 && Number.isSafeInteger(row.height) && row.height > 0) {
    return { kind: "image", src: row.src, width: row.width, height: row.height };
  }
  return undefined;
}

export function parseShowcaseMediaMap(value: unknown): ShowcaseMediaMap {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid showcase media map");
  const entries = Object.entries(value).map(([key, item]) => {
    const media = parseShowcaseMediaValue(item);
    if (!isShowcaseMediaKey(key) || !media) throw new Error("Invalid showcase media entry");
    return [key, media] as const;
  });
  return Object.fromEntries(entries);
}
